/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { parseMarkdown } from '../utils/markdown';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { DomController } from './DomController';
import type { NoteService } from './NoteService';
import type { GeminiService } from './GeminiService';
import type { SettingsService } from './SettingsService';
import type { EditorService } from './EditorService';
import type { UIService } from './UIService';
import type { DialogService } from './DialogService';

/**
 * Handles specific, user-initiated actions related to the editor content,
 * like manual AI operations, copying, and downloading.
 */
export class EditorActionService {
    private statusManager: StatusManager;
    
    // Service registry getters
    private get dom() { return ServiceRegistry.get<DomController>('dom'); }
    private get noteService() { return ServiceRegistry.get<NoteService>('noteService'); }
    private get geminiService() { return ServiceRegistry.get<GeminiService>('gemini'); }
    private get settingsService() { return ServiceRegistry.get<SettingsService>('settingsService'); }
    private get editorService() { return ServiceRegistry.get<EditorService>('editorService'); }
    private get uiService() { return ServiceRegistry.get<UIService>('uiService'); }
    private get dialogService() { return ServiceRegistry.get<DialogService>('dialogService'); }

    constructor() {
        this.statusManager = new StatusManager(this.dom);
        this.bindEventListeners();
    }
    
    private bindEventListeners(): void {
        this.dom.rewritePopover.addEventListener('click', (e) => this.handleRewriteMenuClick(e));
        this.dom.manualTranslateButton.addEventListener('click', () => this.handleManualTranslate());
        this.dom.copyButton.addEventListener('click', () => this.handleCopyContent());
        this.dom.downloadButton.addEventListener('click', () => this.handleDownloadContent());
        this.dom.generateTitleFirstLineButton.addEventListener('click', () => this.handleGenerateTitleFromFirstLine());
        this.dom.generateTitleMagicButton.addEventListener('click', () => this.handleGenerateMagicTitle());
    }

    private async handleRewriteMenuClick(event: MouseEvent): Promise<void> {
        const target = event.target as HTMLElement;
        const actionItem = target.closest('li[data-action]') as HTMLLIElement;
        if (!actionItem || actionItem.classList.contains('disabled')) return;
    
        const action = actionItem.dataset.action;
        if (!action) return;
    
        // Hide popover after a selection is made
        this.uiService.closeAllPopovers();
    
        switch(action) {
            case 'default-polish':
                await this.handleManualPolish();
                break;
            case 'custom-rewrite':
                this.statusManager.error('Custom rewrite not implemented yet.');
                break;
            default:
                if (action.startsWith('rewrite-')) {
                    await this.handleRewriteAction(action);
                }
                break;
        }
    }
    
    public async handleManualPolish(): Promise<void> {
        if (!this.noteService.currentNote) {
            this.statusManager.error('No active note to polish.');
            return;
        }

        const activeContentEl = this.editorService.getActiveEditor();
        if (!activeContentEl || activeContentEl.classList.contains('placeholder-active')) {
            this.statusManager.error('No content in the active tab to polish.');
            return;
        }

        const contentToPolish = activeContentEl.innerText.trim();
        if (!contentToPolish) {
            this.statusManager.error('No content to polish.');
            return;
        }

        if (activeContentEl.id === 'rawTranscription') {
            this.noteService.currentNote.rawTranscription = contentToPolish;
        }

        this.statusManager.loading('Polishing note');
        try {
            const fastModel = this.settingsService.getFastModel();
            const rewriteModel = this.settingsService.getRewritePolishingModel();
            const noteId = this.noteService.currentNote.id;
            
            // Check if we should detect language or use output language setting
            let targetLanguage: string;
            let statusMessage: string;
            
            if (this.settingsService.outputLanguage === 'autodetect') {
                const { text: detectedLanguage } = await this.geminiService.detectLanguage(contentToPolish, fastModel);
                targetLanguage = detectedLanguage;
                statusMessage = `Language: ${detectedLanguage}`;
            } else {
                targetLanguage = this.settingsService.outputLanguage;
                statusMessage = `Target: ${targetLanguage}`;
            }
            
            this.statusManager.progress('Polishing', statusMessage);

            const { text: polishedText, usageMetadata } = await this.geminiService.getPolishedNote(contentToPolish, targetLanguage, rewriteModel);
            this.uiService.addTokenUsageRecord('Manual Polish', usageMetadata, rewriteModel, noteId);

            this.noteService.currentNote.polishedNote = polishedText;
            this.dom.polishedNote.innerHTML = await parseMarkdown(polishedText);
            this.dom.polishedNote.classList.remove('placeholder-active');
            this.noteService.setDirty(true);
            this.editorService.saveState(true);

            this.noteService.updateTitleFromContent(polishedText);
            this.statusManager.success('Polishing');

            const polishedTabButton = document.querySelector('[data-tab="note"]') as HTMLButtonElement;
            if (polishedTabButton) {
                this.dom.setActiveTab(polishedTabButton);
            }

        } catch (error) {
            ErrorHandler.handle('manual polishing', error, this.dom);
        }
    }

    private async handleRewriteAction(action: string): Promise<void> {
        const activeEditor = this.editorService.getActiveEditor();
        if (!activeEditor || activeEditor.classList.contains('placeholder-active')) {
            this.statusManager.error('No text to rewrite.');
            return;
        }
    
        const originalContent = activeEditor.innerText;
        const originalHtml = activeEditor.innerHTML;
    
        let instruction = '';
        let operationName = '';
    
        switch (action) {
            case 'rewrite-formal':
                instruction = 'Rewrite the text in a formal style.';
                operationName = 'Rewrite (Formal)';
                break;
            case 'rewrite-informal':
                instruction = 'Rewrite the text in an informal, more conversational style.';
                operationName = 'Rewrite (Informal)';
                break;
            case 'rewrite-technical':
                instruction = 'Rewrite the text in a technical style, using precise and professional language appropriate for a knowledgeable audience.';
                operationName = 'Rewrite (Technical)';
                break;
            default:
                this.statusManager.error(`Rewrite action "${action}" not implemented yet.`);
                return;
        }
    
        const rewriteFn = async (): Promise<string> => {
            const fastModel = this.settingsService.getFastModel();
            const rewriteModel = this.settingsService.getRewritePolishingModel();
            const noteId = this.noteService.currentNote?.id ?? null;
            
            // Check if we should detect language or use output language setting
            let targetLanguage: string;
            
            if (this.settingsService.outputLanguage === 'autodetect') {
                const { text: language } = await this.geminiService.detectLanguage(originalContent, fastModel);
                targetLanguage = language;
            } else {
                targetLanguage = this.settingsService.outputLanguage;
            }
            
            const { text: rewrittenText, usageMetadata } = await this.geminiService.rewriteText(originalContent, targetLanguage, rewriteModel, instruction);
            this.uiService.addTokenUsageRecord(operationName, usageMetadata, rewriteModel, noteId);
            return await parseMarkdown(rewrittenText);
        };
    
        await this.dialogService.showRewriteDialogForFullContent(operationName, originalHtml, rewriteFn);
    }

    public async handleManualTranslate(): Promise<void> {
        if (!this.noteService.currentNote) {
            this.statusManager.error('No active note to translate.');
            return;
        }
        this.dom.translatePopover.classList.add('hidden');
        this.statusManager.progress('Translating', this.settingsService.targetLanguage);

        this.editorService.syncActiveContentToModel();
        this.noteService.syncUItoModel(false);

        const polishedContentHTML = this.dom.polishedNote.classList.contains('placeholder-active') ? '' : this.dom.polishedNote.innerHTML.trim();
        const rawContentText = this.noteService.currentNote.rawTranscription;

        if ((!this.settingsService.translatePolished || !polishedContentHTML) && (!this.settingsService.translateRaw || !rawContentText)) {
            this.statusManager.error('No content selected for translation.');
            return;
        }

        try {
            const model = this.settingsService.getDefaultModel();
            const noteId = this.noteService.currentNote.id;
            const targetLanguage = this.settingsService.targetLanguage;
            
            // Check if we should skip translation due to output language setting
            let shouldTranslate = true;
            let skipReason = '';
            
            if (this.settingsService.outputLanguage !== 'autodetect') {
                // User has specified an output language, check if content matches
                const fastModel = this.settingsService.getFastModel();
                
                if (this.settingsService.translatePolished && polishedContentHTML) {
                    const { text: detectedLanguage } = await this.geminiService.detectLanguage(this.dom.polishedNote.innerText, fastModel);
                    if (detectedLanguage === targetLanguage) {
                        shouldTranslate = false;
                        skipReason = 'Polished content already in target language';
                    }
                }
                
                if (shouldTranslate && this.settingsService.translateRaw && rawContentText) {
                    const { text: detectedLanguage } = await this.geminiService.detectLanguage(rawContentText, fastModel);
                    if (detectedLanguage === targetLanguage) {
                        shouldTranslate = false;
                        skipReason = 'Raw content already in target language';
                    }
                }
                
                if (!shouldTranslate) {
                    this.statusManager.success(`Translation skipped: ${skipReason}`);
                    return;
                }
            }

            const { text: translatedPolished, usageMetadata: polishedMeta } = this.settingsService.translatePolished && polishedContentHTML ? await this.geminiService.getTranslation(polishedContentHTML, null, targetLanguage, model, 'html') : { text: '', usageMetadata: undefined };
            const { text: translatedRaw, usageMetadata: rawMeta } = this.settingsService.translateRaw && rawContentText ? await this.geminiService.getTranslation(rawContentText, null, targetLanguage, model, 'text') : { text: '', usageMetadata: undefined };
            
            this.uiService.addTokenUsageRecord('Translate Polished', polishedMeta, model, noteId);
            this.uiService.addTokenUsageRecord('Translate Raw', rawMeta, model, noteId);
            
            this.noteService.currentNote.translatedNote = this.combineTranslations(translatedPolished, translatedRaw);
            this.noteService.setDirty(true);
            await this.updateTranslationUI();
            this.editorService.saveState(true);
            await this.noteService.saveCurrentNote(false); // Save but keep dirty
            this.statusManager.success('Translation');
        } catch (error) {
            ErrorHandler.handle('manual translation', error, this.dom);
        }
    }
    
    public async translateCurrentNote(sourceLanguage: string, isAppendMode = false): Promise<void> {
        if (!this.noteService.currentNote) {
            return;
        }
        
        // Check if we should skip translation based on output language setting
        if (this.settingsService.outputLanguage !== 'autodetect') {
            // User has specified output language, check if source matches target
            if (sourceLanguage.toLowerCase() === this.settingsService.targetLanguage.toLowerCase()) {
                this.dom.translatedTabButton.classList.add('hidden');
                this.statusManager.success(`Translation skipped: Content already in target language (${this.settingsService.targetLanguage})`);
                return;
            }
        } else {
            // Autodetect mode - use original logic
            if (sourceLanguage.toLowerCase() === this.settingsService.targetLanguage.toLowerCase()) {
                this.dom.translatedTabButton.classList.add('hidden');
                return;
            }
        }
        this.statusManager.progress('Translating', this.settingsService.targetLanguage);
        try {
            const model = this.settingsService.getDefaultModel();
            const noteId = this.noteService.currentNote.id;
            const targetLanguage = this.settingsService.targetLanguage;
            let textToTranslatePolished = this.settingsService.translatePolished ? (this.noteService.currentNote.polishedNote) : '';
            let textToTranslateRaw = this.settingsService.translateRaw ? (this.noteService.currentNote.rawTranscription) : '';
    
            const { text: translatedPolished, usageMetadata: polishedMeta } = textToTranslatePolished ? await this.geminiService.getTranslation(textToTranslatePolished, sourceLanguage, targetLanguage, model) : { text: '', usageMetadata: undefined };
            const { text: translatedRaw, usageMetadata: rawMeta } = textToTranslateRaw ? await this.geminiService.getTranslation(textToTranslateRaw, sourceLanguage, targetLanguage, model) : { text: '', usageMetadata: undefined };
            
            this.uiService.addTokenUsageRecord('Translate Polished', polishedMeta, model, noteId);
            this.uiService.addTokenUsageRecord('Translate Raw', rawMeta, model, noteId);
            const combinedTranslation = this.combineTranslations(translatedPolished, translatedRaw);
    
            if (isAppendMode) {
                const activeEditor = this.editorService.getActiveEditor();
                if (activeEditor?.id === 'translatedNote') {
                    this.editorService.insertContent(await parseMarkdown(combinedTranslation));
                } else {
                    this.noteService.currentNote.translatedNote += `\n\n${combinedTranslation}`;
                }
            } else {
                this.noteService.currentNote.translatedNote = combinedTranslation;
            }
            
            this.noteService.setDirty(true);
            await this.updateTranslationUI();
            this.editorService.saveState(true);
            await this.noteService.saveCurrentNote(false); // Save but keep dirty
        } catch (error) {
            ErrorHandler.handle('translation', error, this.dom);
            this.noteService.updateTabVisibility();
        }
    }
    
    private combineTranslations(polished: string, raw: string): string {
        if (polished && raw) return `## Polished Note (Translated)\n\n${polished}\n\n---\n\n## Raw Transcription (Translated)\n\n${raw}`;
        return polished || raw;
    }

    private async updateTranslationUI(): Promise<void> {
        if (this.noteService.currentNote && this.noteService.currentNote.translatedNote.trim()) {
            this.dom.translatedNote.innerHTML = await parseMarkdown(this.noteService.currentNote.translatedNote);
            this.dom.translatedNote.classList.remove('placeholder-active');
            this.noteService.updateTabVisibility();
            requestAnimationFrame(() => {
                this.dom.setActiveTab(this.dom.translatedTabButton);
                this.editorService.updateActiveEditorForHistory();
            });
        } else {
            this.noteService.updateTabVisibility();
        }
    }
    
    public handleCopyContent(): void {
        this.editorService.syncActiveContentToModel();
        this.noteService.syncUItoModel(false);
        const activeTabContentEl = document.querySelector('.note-content.active') as HTMLElement;
        if (!activeTabContentEl || !this.noteService.currentNote || activeTabContentEl.classList.contains('placeholder-active')) {
            this.dom.updateStatus('Nothing to copy.');
            return;
        }

        let contentToCopy = '';
        switch (activeTabContentEl.id) {
            case 'polishedNote':
                contentToCopy = this.noteService.currentNote.polishedNote;
                break;
            case 'rawTranscription':
                contentToCopy = this.noteService.currentNote.rawTranscription;
                break;
            case 'translatedNote':
                contentToCopy = this.noteService.currentNote.translatedNote;
                break;
        }

        if (!contentToCopy.trim()) {
            this.statusManager.error('Nothing to copy.');
            return;
        }

        navigator.clipboard.writeText(contentToCopy.trim()).then(() => {
            this.statusManager.success('Copied to clipboard!');
            const originalIcon = this.dom.copyButton.innerHTML;
            this.dom.copyButton.innerHTML = '<i class="fas fa-check"></i>';
            this.dom.copyButton.classList.add('success');
            setTimeout(() => {
                this.dom.copyButton.innerHTML = originalIcon;
                this.dom.copyButton.classList.remove('success');
            }, 2000);
        }).catch(err => {
            ErrorHandler.handle('copying text', err, this.dom);
        });
    }

    public handleDownloadContent(): void {
        this.editorService.syncActiveContentToModel();
        this.noteService.syncUItoModel(false);
        const activeTabContentEl = document.querySelector('.note-content.active') as HTMLElement;
        if (!activeTabContentEl || !this.noteService.currentNote || activeTabContentEl.classList.contains('placeholder-active')) {
            this.statusManager.error('Nothing to download.');
            return;
        }
        let contentToDownload = '';
        switch (activeTabContentEl.id) {
            case 'polishedNote': contentToDownload = this.noteService.currentNote.polishedNote; break;
            case 'rawTranscription': contentToDownload = this.noteService.currentNote.rawTranscription; break;
            case 'translatedNote': contentToDownload = this.noteService.currentNote.translatedNote; break;
        }
        const noteTitle = (this.noteService.currentNote.title || 'Untitled_Note').replace(/[^a-z0-9_ -]/gi, '_').replace(/ /g, '_');
        const filename = `${noteTitle}.md`;
        const blob = new Blob([contentToDownload], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.statusManager.success(`Downloaded ${filename}.`);
    }

    private async handleGenerateTitleFromFirstLine(): Promise<void> {
        this.editorService.syncActiveContentToModel();
        const content = this.noteService.currentNote?.polishedNote || this.noteService.currentNote?.rawTranscription;
        if (!content) {
            this.statusManager.error('No content to generate title from.');
            return;
        }
        const firstLine = content.split('\n').find(line => line.trim() !== '');
        if (firstLine) {
            const newTitle = firstLine.replace(/^[#*-\s>]+/, '').trim();
            this.dom.editorTitle.textContent = newTitle;
            this.dom.editorTitle.classList.remove('placeholder-active');
            this.noteService.syncUItoModel(false);
            await this.noteService.saveCurrentNote();
            const saveLocation = this.noteService.currentNote?.driveFileId ? 'to Google Drive' : 'locally';
            this.statusManager.custom(`Note saved ${saveLocation}.`);
        }
    }

    private async handleGenerateMagicTitle(): Promise<void> {
        this.editorService.syncActiveContentToModel();
        const content = this.noteService.currentNote?.polishedNote || this.noteService.currentNote?.rawTranscription;
        if (!content || content.trim().length < 20) {
            this.statusManager.error('Not enough content to generate a title.');
            return;
        }

        const magicButton = this.dom.generateTitleMagicButton;
        magicButton.classList.add('loading');
        this.statusManager.loading('Generating title');

        try {
            const snippet = content.split(' ').slice(0, 200).join(' ');
            const model = this.settingsService.getTitleGeneratorModel();
            const noteId = this.noteService.currentNote?.id ?? null;
            const { text: newTitle, usageMetadata } = await this.geminiService.generateTitle(snippet, model);
            this.uiService.addTokenUsageRecord('Generate Title', usageMetadata, model, noteId);
            this.dom.editorTitle.textContent = newTitle;
            this.dom.editorTitle.classList.remove('placeholder-active');
            this.noteService.syncUItoModel(false);
            await this.noteService.saveCurrentNote();
            const saveLocation = this.noteService.currentNote?.driveFileId ? 'to Google Drive' : 'locally';
            this.dom.updateStatusWithTimeout(`Title generated. Note saved ${saveLocation}.`);
        } catch (e) {
            console.error("Title generation failed:", e);
            this.dom.updateStatus('Failed to generate title.');
        } finally {
            magicButton.classList.remove('loading');
        }
    }
}